import * as React from 'react';

import {
  enumSalesLoadLineItemPhase,
  enumUnit,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, Col, ContentSection, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LucidePlusCircle } from 'lucide-react';

import { useLineItemPhases } from '../../hooks/useLineItemPhases';
import { useLoadLineItemsManager } from '../../hooks/useLoadLineItemsManager';
import { useSalesOrderLineItemsData } from '../../hooks/useSalesOrderLineItemsData';
import { toWeightUnitType } from '../../hooks/useWeightUnitManager';
import { toWeight } from '../../utils/weight';
import { DebugPanel } from '../loads/LoadDebugPanel';

import {
  SalesOrderLoadTable,
  StandaloneLoadTable,
  StandaloneLoadWithSOTable,
} from './LineItems';

import {
  $loadForm,
  $loadMaterialsStatus,
} from '~/modules/trade/_state/loadForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType, LoadFormState } from '~/modules/trade/types/load';
import { loadHasBeenShipped } from '~/modules/trade/utils/load';
import { $defaultUnitSystemUnit } from '~/shared/state/settings.state';

type Props = {
  disabled?: boolean;
  className?: string;
  itemsSectionClassName?: string;
  effectiveWeightDisplayUnit?: WeightDisplayUnit;
};

const loadMaterialsFormSelector = (state: LoadFormState) => ({
  lineItems: state.lineItems,
  facilityId: state.facilityId,
  salesOrderId: state.salesOrderId,
  weightDisplayUnit: state.weightDisplayUnit,
  status: state.status,
});

export const LoadMaterialsForm = ({
  effectiveWeightDisplayUnit,
  disabled = false,
  itemsSectionClassName,
  className,
}: Props) => {
  const t = useTranslations();
  const selectedFormAtom = React.useMemo(
    () => selectAtom($loadForm, loadMaterialsFormSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($loadForm);
  const loadMaterialsStatus = useAtomValue($loadMaterialsStatus);
  const isLoadShipped = loadHasBeenShipped(form.status);

  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);

  const weightUnit = toWeightUnitType(effectiveWeightDisplayUnit);

  const { lineItemsByPhase } = useLineItemPhases({
    lineItems: form.lineItems,
    loadPhaseType: loadMaterialsStatus,
  });

  const { getSeparatedLineItems, getRemovedSalesOrderLineItems } =
    useSalesOrderLineItemsData();

  const salesOrderLineItemsList = React.useMemo(
    () => getSeparatedLineItems(lineItemsByPhase).salesOrderLineItems,
    [lineItemsByPhase, getSeparatedLineItems],
  );

  const standaloneLineItemsList = React.useMemo(
    () => getSeparatedLineItems(lineItemsByPhase).standaloneLineItems,
    [lineItemsByPhase, getSeparatedLineItems],
  );

  const removedSalesOrderLineItems = React.useMemo(
    () => getRemovedSalesOrderLineItems(lineItemsByPhase),
    [getRemovedSalesOrderLineItems, lineItemsByPhase],
  );

  const { addItem, addSalesOrderItem, updateItem, deleteItem } =
    useLoadLineItemsManager({
      salesOrderLineItems: salesOrderLineItemsList,
      phase: loadMaterialsStatus,
      removedSalesOrderLineItems,
    });

  const handleChangeRequestedQuantity = React.useCallback(
    (lineItemId: string, value: number) => {
      const targetItem = lineItemsByPhase.find((li) => li.id === lineItemId);
      if (!targetItem) return;

      updateItem(lineItemId, {
        weights: {
          ...targetItem.weights,
          [enumLoadWeightType.Net]: toWeight(
            value,
            targetItem.weights?.[enumLoadWeightType.Net]?.unit ??
              facilityDefaultUnit ??
              enumUnit.Pounds,
          ),
        },
      });
    },
    [updateItem, lineItemsByPhase, facilityDefaultUnit],
  );

  const handleUseRemainingQuantity = React.useCallback(() => {
    salesOrderLineItemsList.forEach((lineItem) => {
      const remainingWeight =
        lineItem.weights?.[enumLoadWeightType.QuantityOwed];
      if (remainingWeight && remainingWeight.value > 0) {
        updateItem(lineItem.id, {
          weights: {
            ...lineItem.weights,
            [enumLoadWeightType.Net]: toWeight(
              remainingWeight.value,
              remainingWeight.unit,
            ),
          },
        });
      }
    });
  }, [salesOrderLineItemsList, updateItem]);

  const handleAddMaterial = React.useCallback(() => {
    addItem('Raw');
  }, [addItem]);

  const handleAddSalesOrderMaterial = React.useCallback(() => {
    addSalesOrderItem('Raw');
  }, [addSalesOrderItem]);

  /*
   * Sync line items with current phase
   */
  React.useEffect(() => {
    updateForm((prevForm) => {
      const updatedLineItems = prevForm.lineItems.map((item) => {
        if (!item.phase) {
          return {
            ...item,
            phase: loadMaterialsStatus,
          };
        }
        return item;
      });

      const hasChanges = updatedLineItems.some(
        (item, index) => item.phase !== prevForm.lineItems[index]?.phase,
      );

      if (!hasChanges) return prevForm;

      return {
        ...prevForm,
        lineItems: updatedLineItems,
      };
    });
  }, [loadMaterialsStatus, updateForm]);

  return (
    <div
      className={cn('@LoadMaterialsForm', 'min-w-[72.5rem] flex-1', className)}
    >
      <ContentSection className={itemsSectionClassName}>
        {/* Load Items Section */}
        {form.facilityId && !form.lineItems.length && (
          <Col gap5 className="my-auto text-center">
            <Col gap1>
              <p className="text-lg">{t.loadsView.form.emptyState.title}</p>
              {!disabled && (
                <p className="text-sm text-neutral/50">
                  {t.loadsView.form.emptyState.description}
                </p>
              )}
            </Col>
            {!disabled && (
              <Row gap3 xCenter>
                <Button
                  variant="outline"
                  color="primary"
                  onPress={handleAddMaterial}
                  size="large"
                >
                  {t.loadsView.form.actions.addMaterial}
                </Button>
              </Row>
            )}
          </Col>
        )}

        {form.facilityId &&
          !!lineItemsByPhase.length &&
          loadMaterialsStatus === enumSalesLoadLineItemPhase.Requested && (
            <>
              {/* Sales Order Items Section */}
              {form.salesOrderId && salesOrderLineItemsList.length > 0 && (
                <div
                  className={cn(
                    'flex flex-col gap-4',
                    salesOrderLineItemsList.length > 0 && !isLoadShipped
                      ? 'border-b border-neutral/10 pb-4'
                      : '',
                  )}
                >
                  <SalesOrderLoadTable
                    lineItems={salesOrderLineItemsList}
                    onUpdateLineItem={updateItem}
                    onDeleteLineItem={deleteItem}
                    onAddLineItem={handleAddMaterial}
                    removedSalesOrderLineItems={removedSalesOrderLineItems}
                    onChangeRequestedQuantity={handleChangeRequestedQuantity}
                    onUseRemainingQuantity={handleUseRemainingQuantity}
                    disabled={disabled}
                    hideAddButton
                    status={form.status}
                    weightUnit={weightUnit}
                    facilityDefaultUnit={facilityDefaultUnit ?? enumUnit.Pounds}
                  />

                  {removedSalesOrderLineItems.length > 0 && (
                    <div className="flex items-center">
                      {!disabled && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            color="primary"
                            onPress={handleAddSalesOrderMaterial}
                            icon={<LucidePlusCircle />}
                          >
                            {t.loadsView.form.actions.addMaterial}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Standalone Items Section */}
              {standaloneLineItemsList.length > 0 && (
                <div className="flex flex-col gap-4">
                  {form.salesOrderId ? (
                    <StandaloneLoadWithSOTable
                      lineItems={standaloneLineItemsList}
                      onUpdateLineItem={updateItem}
                      onDeleteLineItem={deleteItem}
                      onAddLineItem={handleAddMaterial}
                      disabled={disabled}
                      hideAddButton
                      weightUnit={weightUnit}
                      facilityDefaultUnit={
                        facilityDefaultUnit ?? enumUnit.Pounds
                      }
                    />
                  ) : (
                    <StandaloneLoadTable
                      lineItems={standaloneLineItemsList}
                      onUpdateLineItem={updateItem}
                      onDeleteLineItem={deleteItem}
                      onAddLineItem={handleAddMaterial}
                      disabled={disabled}
                      hideAddButton
                      weightUnit={weightUnit}
                      facilityDefaultUnit={
                        facilityDefaultUnit ?? enumUnit.Pounds
                      }
                    />
                  )}
                </div>
              )}

              {/* Show add buttons when there are no standalone items but there's a sales order */}
              {!disabled && !isLoadShipped && (
                <div className="flex flex-col">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      color="primary"
                      onPress={handleAddMaterial}
                      icon={<LucidePlusCircle />}
                    >
                      {form.salesOrderId
                        ? t.loadsView.form.actions.addOtherMaterial
                        : t.loadsView.form.actions.addMaterial}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
      </ContentSection>
      <DebugPanel
        hidden
        formLineItems={form.lineItems}
        salesOrderLineItems={salesOrderLineItemsList}
      />
    </div>
  );
};
